import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Wand2,
  Download,
  Image as ImageIcon,
  Sparkles,
  Copy,
  Settings,
  FileImage,
  Palette,
  Zap,
  Star,
  ChevronDown,
  Eye,
  EyeOff,
  Maximize2,
  X,
  Clock,
  Check,
  Share2,
  ChevronRight,
  ChevronLeft,
  Upload,
  Trash2,
  Coins,
  AlertTriangle,
  CreditCard,
  Layers,
  Plus,
  Link,
  Gauge
} from 'lucide-react';
import { generateImage } from '../services/falApi';
import { downloadFile, copyToClipboard, shareContent } from '../utils';
import { useModels } from '../hooks/useModels';
import { useCredits } from '../hooks/useCredits';
import ModelPicker from './ModelPicker';
import InPaint from './InPaint';
import { useNavigate } from 'react-router-dom';

const RATIO_PIXELS = {
  '21:9': { width: 2100, height: 900 },
  '16:9': { width: 1920, height: 1080 },
  '4:3':  { width: 1440, height: 1080 },
  '3:2':  { width: 1536, height: 1024 },
  '5:4':  { width: 1280, height: 1024 },
  '1:1':  { width: 1024, height: 1024 },
  '4:5':  { width: 1024, height: 1280 },
  '2:3':  { width: 1024, height: 1536 },
  '3:4':  { width: 1080, height: 1440 },
  '9:16': { width: 1080, height: 1920 },
  '9:21': { width: 900, height: 2100 },
  '2:1':  { width: 2048, height: 1024 },
  '1:2':  { width: 1024, height: 2048 },
};

const imageSchema = z.object({
  prompt: z.string().min(1, "Il prompt è obbligatorio").max(5000, "Il prompt non può superare i 5000 caratteri"),
  negativePrompt: z.string().max(2000, "Il prompt negativo non può superare i 2000 caratteri").optional(),
  model: z.string().default("fal-ai/flux-1/schnell"),
  width: z.number().min(256).max(2816).default(1024),
  height: z.number().min(256).max(2816).default(1024),
  seed: z.number().optional(),
  safetyChecker: z.boolean().default(true),
  format: z.enum(["jpeg", "png", "webp"]).default("jpeg"),
  imageInput: z.any().optional(),
  strength: z.number().min(0.1).max(1.0).default(0.8).optional(),
  maskUrl: z.string().optional()
});

export default function ImageGenerator() {
  const [generatedImages, setGeneratedImages] = useState([]);
  const [error, setError] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [showNegativePrompt, setShowNegativePrompt] = useState(false);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [isModelSelectorOpen, setIsModelSelectorOpen] = useState(false);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [selectedAspectRatio, setSelectedAspectRatio] = useState('1:1');
  const [showCreditsBanner, setShowCreditsBanner] = useState(false);
  const [showInPaint, setShowInPaint] = useState(false);
  const [maskUrl, setMaskUrl] = useState(null);
  const [loras, setLoras] = useState([{ path: '', scale: 1.0 }]);
  const [acceleration, setAcceleration] = useState('regular');
  const [enablePromptExpansion, setEnablePromptExpansion] = useState(false);
  const [guidanceScale, setGuidanceScale] = useState(2.5);
  const [imageSizeResolution, setImageSizeResolution] = useState('1K');
  const [mobileTab, setMobileTab] = useState('form');


  // Hooks
  const { getModelsByCategory, getModelById } = useModels();
  const { credits, refreshCredits } = useCredits();
  const imageModels = getModelsByCategory('image');
  const navigate = useNavigate();
  
  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm({
    resolver: zodResolver(imageSchema),
    defaultValues: {
      model: "fal-ai/flux-1/schnell",
      width: 1024,
      height: 1024,
      safetyChecker: true,
      format: "jpeg",
      strength: 0.8
    }
  });

  const selectedModel = getModelById(watch("model"));
  const requiredCredits = selectedModel?.credits || 0;
  const hasEnoughCredits = credits >= requiredCredits;
  const currentWidth = watch("width");
  const currentHeight = watch("height");
  const imageSizeMode = selectedModel?.imageSizeMode || 'flux_preset';

  // Aspect ratios predefiniti con supporto 2K
  const aspectRatios = [
    { label: '1:1', ratio: '1:1', width: 1080, height: 1080 },
    { label: '3:4', ratio: '3:4', width: 1080, height: 1440 },
    { label: '4:3', ratio: '4:3', width: 1440, height: 1080 },
    { label: '16:9', ratio: '16:9', width: 1920, height: 1080 },
    { label: '4:5', ratio: '4:5', width: 1152, height: 1440 },
    { label: '9:16', ratio: '9:16', width: 1080, height: 1920 },
    { label: 'Custom', ratio: 'custom', width: null, height: null }
  ];

  // Funzione per controllare se le dimensioni corrispondono a un aspect ratio predefinito
  const getCurrentAspectRatio = () => {
    for (const ar of aspectRatios) {
      if (ar.width === currentWidth && ar.height === currentHeight) {
        return ar.ratio;
      }
    }
    return 'custom';
  };

  // Effetto per aggiornare l'aspect ratio selezionato quando cambiano le dimensioni
  useEffect(() => {
    const currentRatio = getCurrentAspectRatio();
    setSelectedAspectRatio(currentRatio);
  }, [currentWidth, currentHeight]);

  // Funzione per impostare un aspect ratio
  const setAspectRatio = (ratio) => {
    const aspectRatio = aspectRatios.find(ar => ar.ratio === ratio);
    if (aspectRatio && aspectRatio.width && aspectRatio.height) {
      setValue("width", aspectRatio.width);
      setValue("height", aspectRatio.height);
    }
    setSelectedAspectRatio(ratio);
  };

  const setRatioFromString = (ratio) => {
    const px = RATIO_PIXELS[ratio];
    if (px) { setValue('width', px.width); setValue('height', px.height); }
  };

  const getCurrentRatioString = () => {
    const r = currentWidth / currentHeight;
    const opts = selectedModel?.imageSizeOptions || [];
    if (!opts.length) return null;
    return opts.reduce((best, opt) => {
      const [n, d] = opt.split(':').map(Number);
      const [bn, bd] = best.split(':').map(Number);
      return Math.abs(r - n/d) < Math.abs(r - bn/bd) ? opt : best;
    });
  };

  // snap dimensions when switching to a model with a different size mode
  useEffect(() => {
    const mode = selectedModel?.imageSizeMode || 'flux_preset';
    if (mode === 'aspect_ratio') {
      const firstRatio = selectedModel?.imageSizeOptions?.[0] || '1:1';
      const px = RATIO_PIXELS[firstRatio] || { width: 1024, height: 1024 };
      setValue('width', px.width);
      setValue('height', px.height);
    } else if (mode === 'openai') {
      setValue('width', 1024);
      setValue('height', 1024);
    }
    if (selectedModel?.imageSizeResolutions) {
      setImageSizeResolution(selectedModel.imageSizeResolutions[0]);
    }
  }, [selectedModel?.id]);

  const downloadImage = async (imageUrl, index) => {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      downloadFile(url, `ai-generated-${Date.now()}-${index + 1}.${watch("format")}`);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Errore nel download:', error);
    }
  };

  const copyImageUrl = async (imageUrl) => {
    try {
      await copyToClipboard(imageUrl);
    } catch (error) {
      console.error('Errore nella copia:', error);
    }
  };

  const shareImage = async (imageUrl) => {
    try {
      await shareContent({
        title: 'Immagine generata con AI',
        url: imageUrl
      });
    } catch (error) {
      console.error('Errore nella condivisione:', error);
      copyImageUrl(imageUrl);
    }
  };

  const generateRandomSeed = () => {
    const randomSeed = Math.floor(Math.random() * 1000000);
    setValue("seed", randomSeed);
  };

  const addLora = () => {
    const max = selectedModel?.maxLoras || 3;
    if (loras.length < max) setLoras(prev => [...prev, { path: '', scale: 1.0 }]);
  };
  const removeLora = (i) => setLoras(prev => prev.filter((_, idx) => idx !== i));
  const updateLora = (i, field, value) =>
    setLoras(prev => prev.map((l, idx) => idx === i ? { ...l, [field]: value } : l));

  const handleMaskCreated = (maskDataUrl) => {
    setMaskUrl(maskDataUrl);
  };

  // Funzione per comprimere immagini
  const compressImage = (file, maxSizeKB = 500, quality = 0.8) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        // Calcola le nuove dimensioni mantenendo l'aspect ratio
        let { width, height } = img;
        const maxDimension = 2816; // Dimensione massima per evitare payload troppo grandi
        
        if (width > height && width > maxDimension) {
          height = (height * maxDimension) / width;
          width = maxDimension;
        } else if (height > maxDimension) {
          width = (width * maxDimension) / height;
          height = maxDimension;
        }
        
        canvas.width = width;
        canvas.height = height;
        
        // Disegna l'immagine ridimensionata
        ctx.drawImage(img, 0, 0, width, height);
        
        // Converti in blob con compressione
        canvas.toBlob((blob) => {
          // Se ancora troppo grande, riduci ulteriormente la qualità
          if (blob.size > maxSizeKB * 2816 && quality > 0.1) {
            compressImage(file, maxSizeKB, quality - 0.1).then(resolve);
          } else {
            resolve(blob);
          }
        }, 'image/jpeg', quality);
      };
      
      img.src = URL.createObjectURL(file);
    });
  };

  // Funzione per gestire l'upload dell'immagine con validazione e compressione
  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    // Validazione dimensione file (10MB)
    const maxSizeBytes = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSizeBytes) {
      alert('Il file è troppo grande. La dimensione massima consentita è 10MB.');
      event.target.value = ''; // Reset input
      return;
    }
    
    // Validazione tipo file
    if (!file.type.startsWith('image/')) {
      alert('Per favore seleziona un file immagine valido.');
      event.target.value = '';
      return;
    }
    
    try {
      // Comprimi l'immagine se è troppo grande (>500KB)
      let processedFile = file;
      if (file.size > 400 * 2816) {
        const compressedBlob = await compressImage(file);
        processedFile = new File([compressedBlob], file.name, { type: 'image/jpeg' });
      }
      
      setUploadedImage(processedFile);
      
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
        
        // Crea un elemento immagine per ottenere le dimensioni naturali
        const img = new Image();
        img.onload = () => {
          let { naturalWidth: width, naturalHeight: height } = img;
          
          // Calcola l'aspect ratio
          const aspectRatio = width / height;
          
          // Limiti del modello (solitamente max 2048x2048 per FLUX)
          const maxDimension = 2048;
          const minDimension = 256;
          
          // Se l'immagine è troppo grande, ridimensiona mantenendo l'aspect ratio
          if (width > maxDimension || height > maxDimension) {
            if (width > height) {
              width = maxDimension;
              height = Math.round(maxDimension / aspectRatio);
            } else {
              height = maxDimension;
              width = Math.round(maxDimension * aspectRatio);
            }
          }
          
          // Se l'immagine è troppo piccola, ridimensiona mantenendo l'aspect ratio
          if (width < minDimension || height < minDimension) {
            if (width < height) {
              width = minDimension;
              height = Math.round(minDimension / aspectRatio);
            } else {
              height = minDimension;
              width = Math.round(minDimension * aspectRatio);
            }
          }
          
          // Assicurati che le dimensioni siano multiple di 8 (requisito comune per i modelli di diffusione)
          width = Math.round(width / 8) * 8;
          height = Math.round(height / 8) * 8;
          
          // Imposta le dimensioni nel form
          setValue('width', width);
          setValue('height', height);
          
          // Aggiorna l'aspect ratio selezionato
          const ratio = width / height;
          if (Math.abs(ratio - 1) < 0.1) {
            setSelectedAspectRatio('1:1');
          } else if (Math.abs(ratio - 16/9) < 0.1) {
            setSelectedAspectRatio('16:9');
          } else if (Math.abs(ratio - 9/16) < 0.1) {
            setSelectedAspectRatio('9:16');
          } else if (Math.abs(ratio - 4/3) < 0.1) {
            setSelectedAspectRatio('4:3');
          } else if (Math.abs(ratio - 3/4) < 0.1) {
            setSelectedAspectRatio('3:4');
          } else {
            setSelectedAspectRatio('custom');
          }
          
          console.log(`📐 Dimensioni immagine impostate automaticamente: ${width}x${height} (originale: ${img.naturalWidth}x${img.naturalHeight})`);
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(processedFile);
      
    } catch (error) {
      console.error('Errore durante la compressione dell\'immagine:', error);
      alert('Errore durante l\'elaborazione dell\'immagine. Riprova con un file diverso.');
      event.target.value = '';
    }
  };

  // Funzione per rimuovere l'immagine caricata
  const removeUploadedImage = () => {
    setUploadedImage(null);
    setImagePreview(null);
  };

  // Funzione per convertire file in base64
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  };

  const onSubmit = async (data) => {
    if (isGenerating) return;
    
    console.log('🚀 Iniziando generazione immagine:', { model: data.model, hasImageInput: !!uploadedImage });
    
    // Se c'è già un'immagine, spostala immediatamente nello storico
    if (generatedImages.length > 0) {
      // Non facciamo nulla qui, la logica di spostamento è gestita dal rendering
    }
    
    if ((selectedModel?.inPaint || selectedModel?.isEditingModel || selectedModel?.requiresImageInput) && !uploadedImage) {
      setError(
        selectedModel?.inPaint
          ? 'I modelli di inpainting richiedono un\'immagine di input. Carica un\'immagine prima di procedere.'
          : 'I modelli di editing richiedono un\'immagine di input. Carica un\'immagine prima di procedere.'
      );
      return;
    }

    if (selectedModel?.inPaint && !maskUrl) {
      setError('Crea una maschera per indicare le aree da modificare prima di procedere.');
      return;
    }

    setMobileTab('results');
    setIsGenerating(true);
    setError(null);
    setShowCreditsBanner(false);

    // Client-side credit guard (UX only — backend enforces server-side)
    if (!hasEnoughCredits) {
      setIsGenerating(false);
      setShowCreditsBanner(true);
      return;
    }

    try {
      let imageUrl = null;

      if (uploadedImage && selectedModel?.supportsImageInput) {
        const base64Image = await fileToBase64(uploadedImage);
        imageUrl = base64Image;
      }

      const validLoras = selectedModel?.supportsLoras
        ? loras.filter(l => l.path.trim())
        : undefined;

      // Backend deducts credits atomically — no separate /credits/deduct call needed
      const result = await generateImage({
        prompt: data.prompt,
        negative_prompt: data.negativePrompt,
        model: data.model,
        image_size: { width: data.width, height: data.height },
        num_images: 1,
        enable_safety_checker: data.safetyChecker,
        seed: data.seed,
        image_format: data.format,
        ...(imageUrl && { image_url: imageUrl }),
        ...(selectedModel?.supportsImageInput && data.strength && { strength: data.strength }),
        ...(selectedModel?.inPaint && maskUrl && { mask_url: maskUrl }),
        ...(validLoras?.length && { loras: validLoras }),
        ...(selectedModel?.defaultGuidanceScale != null && { guidance_scale: guidanceScale }),
        ...(selectedModel?.supportsAcceleration && { acceleration }),
        ...(selectedModel?.supportsPromptExpansion && { enable_prompt_expansion: enablePromptExpansion }),
        ...(selectedModel?.imageSizeResolutions && { resolution: imageSizeResolution }),
      });

      if (result.success && result.data?.images && result.data.images.length > 0) {
        const imageUrls = result.data.images.map(img => img.url);
        setGeneratedImages(prev => [...imageUrls, ...prev]);
        // Update local credits state from backend response
        if (result.remainingCredits !== undefined) {
          refreshCredits();
        }
      } else {
        if (result.error?.includes('insufficienti')) {
          setShowCreditsBanner(true);
        }
        throw new Error(result.error || 'Errore nella generazione dell\'immagine');
      }

    } catch (error) {
      console.error("Errore nella generazione:", error);
      setError(error.message || 'Errore sconosciuto');
      refreshCredits();
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto">
      {/* Mobile Tab Switcher */}
      <div className="xl:hidden mb-4 flex p-1 rounded-2xl" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
        <button
          type="button"
          onClick={() => setMobileTab('form')}
          className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold transition-all ${mobileTab === 'form' ? 'pill-active' : ''}`}
          style={{ color: mobileTab === 'form' ? '#fff' : 'rgba(255,255,255,0.45)' }}
        >
          <Wand2 size={14} />
          Genera
        </button>
        <button
          type="button"
          onClick={() => setMobileTab('results')}
          className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold transition-all ${mobileTab === 'results' ? 'pill-active' : ''}`}
          style={{ color: mobileTab === 'results' ? '#fff' : 'rgba(255,255,255,0.45)' }}
        >
          <ImageIcon size={14} />
          Risultati
          {generatedImages.length > 0 && (
            <span className="ml-0.5 text-xs px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(255,255,255,0.2)' }}>{generatedImages.length}</span>
          )}
        </button>
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
      {/* Form Panel */}
      <div className={mobileTab === 'results' ? 'hidden xl:block' : ''}>
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5 }}
        className="space-y-6"
      >
        {/* Header Box - Separata */}
        <div className="panel rounded-3xl p-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="flex items-center justify-between"
          >
            <div className="flex items-center gap-3">
              <div className="p-3 icon-box primary rounded-2xl">
                <Wand2 style={{ color: '#fff' }} size={22} />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'var(--font-heading)', letterSpacing: '-0.025em' }}>Generatore Immagini</h2>
                <p className="text-sm" style={{ color: 'rgba(255,255,255,0.70)' }}>Crea immagini straordinarie con l'AI</p>
              </div>
            </div>

            <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl" style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)' }}>
              <Coins style={{ color: 'rgba(255,255,255,0.90)' }} size={14} />
              <span className="text-white font-semibold text-sm">{credits}</span>
              <span className="text-xs" style={{ color: 'rgba(255,255,255,0.36)' }}>crediti</span>
            </div>
          </motion.div>
        </div>
      
        {/* Safety Checker - nascosto per modelli senza safety param API (es. OpenAI) */}
        {selectedModel?.safetyParam !== "none" && (
          <div className="panel rounded-3xl p-5">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 icon-box rounded-xl">
                    {watch("safetyChecker") ? (
                      <Eye style={{ color: 'rgba(255,255,255,0.80)' }} size={18} />
                    ) : (
                      <EyeOff style={{ color: 'rgba(255,255,255,0.80)' }} size={18} />
                    )}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-semibold text-white">Safety Checker</h3>
                      <AnimatePresence>
                        {!watch("safetyChecker") && (
                          <motion.div
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.8 }}
                            className="px-2 py-0.5 rounded-md text-xs font-bold uppercase tracking-wide"
                            style={{ background: 'rgba(255,50,50,0.15)', border: '1px solid rgba(255,80,80,0.25)', color: 'rgba(255,120,120,1)' }}
                          >
                            NSFW
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                    <p className="text-xs" style={{ color: 'rgba(255,255,255,0.38)' }}>Filtra contenuti espliciti 18+</p>
                  </div>
                </div>

                <motion.button
                  type="button"
                  onClick={() => setValue("safetyChecker", !watch("safetyChecker"))}
                  className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
                    watch("safetyChecker") ? "toggle-on" : "toggle-off"
                  }`}
                  whileTap={{ scale: 0.95 }}
                >
                  <motion.span
                    animate={{ x: watch("safetyChecker") ? 22 : 2 }}
                    transition={{ type: "spring", stiffness: 500, damping: 30 }}
                    className="inline-block h-5 w-5 transform rounded-full shadow-lg"
                    style={{ background: watch("safetyChecker") ? '#000' : 'rgba(255,255,255,0.85)' }}
                  />
                </motion.button>
              </div>
            </motion.div>
          </div>
        )}
      
        {/* Box Generale - Per tutto il resto */}
        <div className="panel rounded-3xl p-7">
          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: -10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: -10 }}
                className="mb-6 p-4 error-banner rounded-2xl"
              >
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: 'rgba(255,80,80,0.9)' }} />
                  <p className="text-sm font-medium">{error}</p>
                </div>
              </motion.div>
            )}

          </AnimatePresence>
          
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Model Selection */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 }}
            >
              <label className="label-sm mb-3 block">Modello AI</label>
              <ModelPicker
                selectedModel={watch("model")}
                onModelSelect={(modelId) => setValue("model", modelId)}
                category="image"
                className="w-full"
              />
            </motion.div>

            
            {/* Prompt Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <label className="label-sm mb-3 block">Prompt Principale</label>
              <div className="relative">
                <textarea
                  {...register("prompt")}
                  placeholder="Descrivi l'immagine che vuoi generare in dettaglio..."
                  className="input-field w-full h-28 px-5 py-4 rounded-2xl resize-none overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
                  maxLength={5000}
                />
                <div className={`absolute bottom-3 right-3 text-xs ${
                  5000 - (watch("prompt")?.length || 0) < 500 ? 'text-red-400' :
                  5000 - (watch("prompt")?.length || 0) < 1000 ? 'text-yellow-400' : ''
                }`} style={{ color: 5000 - (watch("prompt")?.length || 0) >= 1000 ? 'rgba(255,255,255,0.22)' : undefined }}>
                  {5000 - (watch("prompt")?.length || 0)} caratteri rimanenti
                </div>
              </div>
              {errors.prompt && (
                <motion.p 
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="text-red-400 text-sm mt-2 flex items-center gap-1"
                >
                  <div className="w-1 h-1 bg-red-400 rounded-full" />
                  {errors.prompt.message}
                </motion.p>
              )}
            </motion.div>

            {/* LoRA Section */}
            {selectedModel?.supportsLoras && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ delay: 0.33 }}
                className="space-y-3"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <div className="p-2 icon-box rounded-xl">
                      <Layers style={{ color: 'rgba(255,255,255,0.75)' }} size={16} />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-white">LoRA Weights</h3>
                      <p className="text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>HuggingFace repo ID o URL .safetensors · max {selectedModel.maxLoras}</p>
                    </div>
                  </div>
                  {loras.length < selectedModel.maxLoras && (
                    <motion.button
                      type="button"
                      onClick={addLora}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold pill-ghost"
                      whileHover={{ scale: 1.04 }}
                      whileTap={{ scale: 0.96 }}
                    >
                      <Plus size={12} />
                      Aggiungi
                    </motion.button>
                  )}
                </div>

                <div className="space-y-2.5">
                  {loras.map((lora, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: -8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      className="p-3.5 rounded-2xl space-y-3"
                      style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
                    >
                      <div className="flex gap-2 items-center">
                        <div className="flex items-center gap-2 flex-1 px-3 py-2.5 rounded-xl input-field">
                          <Link size={13} style={{ color: 'rgba(255,255,255,0.30)', flexShrink: 0 }} />
                          <input
                            type="text"
                            value={lora.path}
                            onChange={(e) => updateLora(i, 'path', e.target.value)}
                            placeholder={i === 0 ? 'username/lora-name o https://...' : 'secondo LoRA (opzionale)'}
                            className="bg-transparent flex-1 text-sm text-white placeholder:text-white/25 outline-none"
                          />
                        </div>
                        {loras.length > 1 && (
                          <motion.button
                            type="button"
                            onClick={() => removeLora(i)}
                            className="p-2 rounded-xl transition-colors"
                            style={{ color: 'rgba(255,80,80,0.6)' }}
                            whileHover={{ scale: 1.1, color: 'rgba(255,80,80,1)' }}
                            whileTap={{ scale: 0.9 }}
                          >
                            <X size={14} />
                          </motion.button>
                        )}
                      </div>

                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-1.5 w-20 flex-shrink-0">
                          <Gauge size={12} style={{ color: 'rgba(255,255,255,0.35)' }} />
                          <span className="text-xs font-semibold" style={{ color: 'rgba(255,255,255,0.55)' }}>
                            {lora.scale.toFixed(1)}
                          </span>
                        </div>
                        <input
                          type="range"
                          min="0"
                          max="2"
                          step="0.1"
                          value={lora.scale}
                          onChange={(e) => updateLora(i, 'scale', parseFloat(e.target.value))}
                          className="flex-1 h-1.5 rounded-lg appearance-none cursor-pointer slider"
                        />
                        <div className="flex justify-between w-16 text-right">
                          <span className="text-xs" style={{ color: 'rgba(255,255,255,0.20)', fontSize: 10 }}>0</span>
                          <span className="text-xs" style={{ color: 'rgba(255,255,255,0.20)', fontSize: 10 }}>2.0</span>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Image Upload Section */}
            {selectedModel?.supportsImageInput && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35 }}
                className="space-y-4"
              >
                <label className="label-sm mb-3 block">
                  {selectedModel?.inPaint
                    ? 'Immagine da modificare (obbligatoria)'
                    : selectedModel?.isEditingModel
                    ? 'Immagine da editare (obbligatoria)'
                    : 'Immagine di Riferimento (opzionale)'}
                </label>

                {!imagePreview ? (
                  <motion.div
                    className="relative border border-dashed rounded-2xl p-8 text-center cursor-pointer group upload-area"
                    style={{ borderColor: 'rgba(255,255,255,0.25)', background: 'rgba(255,255,255,0.02)' }}
                    whileHover={{ scale: 1.01, backgroundColor: 'rgba(255,255,255,0.05)' }}
                    onClick={() => document.getElementById('image-upload-input').click()}
                  >
                    <input
                      id="image-upload-input"
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                    <div className="flex flex-col items-center gap-4">
                      <div className="p-4 icon-box primary rounded-2xl">
                        <Upload style={{ color: '#fff' }} size={28} />
                      </div>
                      <div>
                        <p className="text-white font-medium text-sm mb-1">Carica un'immagine</p>
                        <p className="text-sm" style={{ color: 'rgba(255,255,255,0.60)' }}>Clicca per selezionare o trascina qui</p>
                        <p className="text-xs mt-1.5" style={{ color: 'rgba(255,255,255,0.40)' }}>PNG, JPG, WEBP · max 10MB</p>
                      </div>
                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="relative rounded-2xl p-4 panel"
                  >
                    <div className="flex items-start gap-4">
                      <div className="relative">
                        <img
                          src={imagePreview}
                          alt="Preview"
                          className="w-20 h-20 object-cover rounded-xl border border-gray-600"
                        />
                        <motion.button
                          type="button"
                          onClick={removeUploadedImage}
                          className="absolute -top-2 -right-2 p-1 bg-red-500 hover:bg-red-600 rounded-full text-white transition-colors"
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                        >
                          <X size={12} />
                        </motion.button>
                      </div>
                      <div className="flex-1">
                        <p className="text-white font-medium">{uploadedImage?.name}</p>
                        <p className="text-gray-400 text-sm">
                          {(uploadedImage?.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                        <motion.button
                          type="button"
                          onClick={() => document.getElementById('image-upload').click()}
                          className="mt-2 text-sm font-medium transition-colors"
                          style={{ color: 'rgba(255,255,255,0.55)' }}
                        >
                          Cambia immagine
                        </motion.button>
                        <input
                          id="image-upload"
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          className="hidden"
                        />
                      </div>
                    </div>
                  </motion.div>
                )}
              </motion.div>
            )}

            {/* InPaint Section */}
            {selectedModel?.inPaint && uploadedImage && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.37 }}
                className="space-y-4"
              >
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-semibold text-gray-200">
                    Maschera InPaint
                  </label>
                  {maskUrl && (
                    <span className="text-xs text-green-400 bg-green-400/10 px-2 py-1 rounded-lg">
                      ✓ Maschera creata
                    </span>
                  )}
                </div>
                
                <div className="panel rounded-2xl p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 icon-box rounded-lg">
                      <Palette size={15} style={{ color: 'rgba(255,255,255,0.70)' }} />
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-white">Editor InPaint</h4>
                      <p className="text-xs" style={{ color: 'rgba(255,255,255,0.36)' }}>
                        Crea una maschera per modificare parti specifiche
                      </p>
                    </div>
                  </div>

                  <motion.button
                    type="button"
                    onClick={() => setShowInPaint(true)}
                    className="pill-ghost w-full py-2.5 px-4 rounded-xl font-medium flex items-center justify-center gap-2"
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                  >
                    <Palette size={15} />
                    <span className="text-sm">{maskUrl ? 'Modifica Maschera' : 'Crea Maschera'}</span>
                  </motion.button>

                  {maskUrl && (
                    <div className="mt-3 p-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                      <div className="flex items-center justify-between">
                        <span className="text-xs" style={{ color: 'rgba(255,255,255,0.38)' }}>Maschera pronta per l'inpainting</span>
                        <button
                          type="button"
                          onClick={() => setMaskUrl(null)}
                          className="text-xs transition-colors"
                          style={{ color: 'rgba(255,80,80,0.7)' }}
                        >
                          Rimuovi
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {/* Advanced Options Collapsible Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="mt-6"
            >
              <motion.button
                type="button"
                onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
                className="pill-ghost w-full flex items-center justify-between p-4 rounded-2xl"
                whileHover={{ scale: 1.005 }}
                whileTap={{ scale: 0.99 }}
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 icon-box rounded-xl">
                    <Settings style={{ color: 'rgba(255,255,255,0.65)' }} size={17} />
                  </div>
                  <div className="text-left">
                    <h3 className="text-white font-semibold text-sm">Opzioni Avanzate</h3>
                    <p className="text-xs" style={{ color: 'rgba(255,255,255,0.36)' }}>Dimensioni, seed, formato</p>
                  </div>
                </div>
                <motion.div
                  animate={{ rotate: showAdvancedOptions ? 180 : 0 }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                  style={{ color: 'rgba(255,255,255,0.40)' }}
                >
                  <ChevronDown size={18} />
                </motion.div>
              </motion.button>

              <AnimatePresence>
                {showAdvancedOptions && (
                  <motion.div
                    initial={{ opacity: 0, height: 0, y: -10 }}
                    animate={{ opacity: 1, height: "auto", y: 0 }}
                    exit={{ opacity: 0, height: 0, y: -10 }}
                    transition={{ duration: 0.4, ease: "easeInOut" }}
                    className="overflow-hidden"
                  >
                    <div className="mt-4 space-y-6 p-6 rounded-2xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                      
                      {/* Strength Control - solo image-to-image puro, non inpaint né editing */}
                      {imagePreview && !selectedModel?.inPaint && !selectedModel?.isEditingModel && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.1 }}
                          className="space-y-3"
                        >
                          <label className="label-sm block">
                            Forza Trasformazione: {watch("strength") || 0.8}
                          </label>
                          <input
                            {...register("strength", { valueAsNumber: true })}
                            type="range"
                            min="0.1"
                            max="1.0"
                            step="0.1"
                            className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                          />
                          <div className="flex justify-between text-xs" style={{ color: 'rgba(255,255,255,0.32)' }}>
                            <span>Conserva originale</span>
                            <span>Trasforma completamente</span>
                          </div>
                        </motion.div>
                      )}

                      {/* Negative Prompt */}
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.15 }}
                      >
                        <button
                          type="button"
                          onClick={() => setShowNegativePrompt(!showNegativePrompt)}
                          className="flex items-center gap-2 transition-colors mb-3"
                          style={{ color: 'rgba(255,255,255,0.45)' }}
                        >
                          {showNegativePrompt ? <EyeOff size={14} /> : <Eye size={14} />}
                          <span className="text-xs font-semibold uppercase tracking-widest">Prompt Negativo</span>
                          <motion.div
                            animate={{ rotate: showNegativePrompt ? 180 : 0 }}
                            transition={{ duration: 0.2 }}
                          >
                            <ChevronDown size={14} />
                          </motion.div>
                        </button>
                        
                        <AnimatePresence>
                          {showNegativePrompt && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: "auto" }}
                              exit={{ opacity: 0, height: 0 }}
                              transition={{ duration: 0.3 }}
                            >
                              <div className="relative">
                                <textarea
                                  {...register("negativePrompt")}
                                  placeholder="Cosa NON vuoi nell'immagine (es: blurry, low quality)..."
                                  className="input-field w-full h-20 px-5 py-4 rounded-2xl resize-none overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
                                  maxLength={5000}
                                />
                                <div className={`absolute bottom-3 right-3 text-xs ${
                                  5000 - (watch("negativePrompt")?.length || 0) < 500 ? 'text-red-400' :
                                  5000 - (watch("negativePrompt")?.length || 0) < 1000 ? 'text-yellow-400' : ''
                                }`} style={{ color: 5000 - (watch("negativePrompt")?.length || 0) >= 1000 ? 'rgba(255,255,255,0.22)' : undefined }}>
                                  {5000 - (watch("negativePrompt")?.length || 0)} caratteri rimanenti
                                </div>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </motion.div>

                      {/* Dimensioni Immagine */}
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                      >
                        <label className="label-sm mb-4 block">Dimensioni</label>

                        {imageSizeMode === 'flux_preset' && (
                          <>
                            {/* Aspect Ratio Selector */}
                            <div className="mb-4">
                              <label className="label-sm mb-3 block" style={{ fontSize: 10 }}>Aspect ratio</label>
                              <div className="flex flex-wrap gap-1.5 mb-4">
                                {aspectRatios.map((ar) => (
                                  <motion.button
                                    key={ar.ratio}
                                    type="button"
                                    onClick={() => setAspectRatio(ar.ratio)}
                                    className={`px-3 py-2 rounded-lg text-xs font-semibold transition-all duration-200 ${
                                      selectedAspectRatio === ar.ratio ? "pill-active" : "pill-ghost"
                                    }`}
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                  >
                                    {ar.label}
                                  </motion.button>
                                ))}
                              </div>
                            </div>

                            {/* Width and Height Inputs */}
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="label-sm mb-2 block" style={{ fontSize: 10 }}>Larghezza</label>
                                <div className="relative">
                                  <input
                                    {...register("width", { valueAsNumber: true })}
                                    type="number"
                                    min="256"
                                    max="2816"
                                    step="8"
                                    className="input-field w-full px-4 py-3 pr-8 rounded-xl"
                                  />
                                  <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-xs" style={{ color: 'rgba(255,255,255,0.28)' }}>px</span>
                                </div>
                              </div>
                              <div>
                                <label className="label-sm mb-2 block" style={{ fontSize: 10 }}>Altezza</label>
                                <div className="relative">
                                  <input
                                    {...register("height", { valueAsNumber: true })}
                                    type="number"
                                    min="256"
                                    max="2816"
                                    step="8"
                                    className="input-field w-full px-4 py-3 pr-8 rounded-xl"
                                  />
                                  <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-xs" style={{ color: 'rgba(255,255,255,0.28)' }}>px</span>
                                </div>
                              </div>
                            </div>
                          </>
                        )}

                        {imageSizeMode === 'aspect_ratio' && (
                          <>
                            <label className="label-sm mb-3 block" style={{ fontSize: 10 }}>Aspect ratio</label>
                            <div className="flex flex-wrap gap-1.5 mb-4">
                              {(selectedModel?.imageSizeOptions || []).map((ratio) => (
                                <motion.button
                                  key={ratio}
                                  type="button"
                                  onClick={() => setRatioFromString(ratio)}
                                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 ${
                                    getCurrentRatioString() === ratio ? 'pill-active' : 'pill-ghost'
                                  }`}
                                  whileHover={{ scale: 1.02 }}
                                  whileTap={{ scale: 0.98 }}
                                >
                                  {ratio}
                                </motion.button>
                              ))}
                            </div>
                            {selectedModel?.imageSizeResolutions && (
                              <>
                                <label className="label-sm mb-3 block" style={{ fontSize: 10 }}>Risoluzione</label>
                                <div className="flex gap-2">
                                  {selectedModel.imageSizeResolutions.map((res) => (
                                    <motion.button
                                      key={res}
                                      type="button"
                                      onClick={() => setImageSizeResolution(res)}
                                      className={`px-4 py-2 rounded-xl text-xs font-bold transition-all duration-200 ${
                                        imageSizeResolution === res ? 'pill-active' : 'pill-ghost'
                                      }`}
                                      whileHover={{ scale: 1.04 }}
                                      whileTap={{ scale: 0.96 }}
                                    >
                                      {res}
                                    </motion.button>
                                  ))}
                                </div>
                              </>
                            )}
                          </>
                        )}

                        {imageSizeMode === 'openai' && (
                          <>
                            <label className="label-sm mb-3 block" style={{ fontSize: 10 }}>Dimensione</label>
                            <div className="flex gap-2">
                              {[
                                { label: 'Quadrata', sub: '1024×1024', width: 1024, height: 1024 },
                                { label: 'Landscape', sub: '1536×1024', width: 1536, height: 1024 },
                                { label: 'Portrait', sub: '1024×1536', width: 1024, height: 1536 },
                              ].map((opt) => (
                                <motion.button
                                  key={opt.label}
                                  type="button"
                                  onClick={() => { setValue('width', opt.width); setValue('height', opt.height); }}
                                  className={`flex-1 py-2.5 px-2 rounded-xl text-xs font-bold transition-all duration-200 flex flex-col items-center gap-0.5 ${
                                    currentWidth === opt.width && currentHeight === opt.height ? 'pill-active' : 'pill-ghost'
                                  }`}
                                  whileHover={{ scale: 1.03 }}
                                  whileTap={{ scale: 0.97 }}
                                >
                                  <span>{opt.label}</span>
                                  <span style={{ fontSize: 9, opacity: 0.6 }}>{opt.sub}</span>
                                </motion.button>
                              ))}
                            </div>
                          </>
                        )}

                      </motion.div>
                      
                      {/* Seed Section */}
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.25 }}
                      >
                        <label className="label-sm mb-3 block">Seed (opzionale)</label>
                        <div className="flex gap-2">
                          <input
                            {...register("seed", {
                              setValueAs: (value) => value === '' ? undefined : Number(value)
                            })}
                            type="number"
                            placeholder="Lascia vuoto per casuale"
                            className="input-field flex-1 px-4 py-3 rounded-xl"
                          />
                          <motion.button
                            type="button"
                            onClick={generateRandomSeed}
                            className="px-4 py-3 icon-box rounded-xl text-white font-medium transition-all"
                            whileHover={{ scale: 1.04 }}
                            whileTap={{ scale: 0.96 }}
                            title="Genera seed casuale"
                          >
                            <Sparkles size={17} style={{ color: 'rgba(255,255,255,0.75)' }} />
                          </motion.button>
                        </div>
                        <p className="text-xs mt-2" style={{ color: 'rgba(255,255,255,0.28)' }}>
                          Stesso seed = risultati riproducibili.
                        </p>
                      </motion.div>

                      {/* Guidance Scale - solo modelli LoRA */}
                      {selectedModel?.defaultGuidanceScale != null && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.27 }}
                          className="space-y-3"
                        >
                          <label className="label-sm block">Guidance Scale: {guidanceScale.toFixed(1)}</label>
                          <input
                            type="range"
                            min="1"
                            max="10"
                            step="0.5"
                            value={guidanceScale}
                            onChange={(e) => setGuidanceScale(parseFloat(e.target.value))}
                            className="w-full h-2 rounded-lg appearance-none cursor-pointer slider"
                          />
                          <div className="flex justify-between text-xs" style={{ color: 'rgba(255,255,255,0.28)' }}>
                            <span>Creativo</span>
                            <span>Fedele al prompt</span>
                          </div>
                        </motion.div>
                      )}

                      {/* Acceleration - solo modelli LoRA */}
                      {selectedModel?.supportsAcceleration && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.28 }}
                        >
                          <label className="label-sm mb-3 block">Accelerazione</label>
                          <div className="flex gap-2">
                            {['none', 'regular', 'high'].map((mode) => (
                              <motion.button
                                key={mode}
                                type="button"
                                onClick={() => setAcceleration(mode)}
                                className={`flex-1 py-2 rounded-xl text-xs font-bold transition-all capitalize ${
                                  acceleration === mode ? 'pill-active' : 'pill-ghost'
                                }`}
                                whileHover={{ scale: 1.03 }}
                                whileTap={{ scale: 0.97 }}
                              >
                                {mode === 'none' ? 'Nessuna' : mode === 'regular' ? 'Regolare' : 'Alta'}
                              </motion.button>
                            ))}
                          </div>
                        </motion.div>
                      )}

                      {/* Prompt Expansion - solo modelli LoRA */}
                      {selectedModel?.supportsPromptExpansion && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.29 }}
                          className="flex items-center justify-between"
                        >
                          <div>
                            <p className="text-sm font-semibold text-white">Prompt Expansion</p>
                            <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.35)' }}>Migliora automaticamente il prompt con AI</p>
                          </div>
                          <motion.button
                            type="button"
                            onClick={() => setEnablePromptExpansion(!enablePromptExpansion)}
                            className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
                              enablePromptExpansion ? 'toggle-on' : 'toggle-off'
                            }`}
                            whileTap={{ scale: 0.95 }}
                          >
                            <motion.span
                              animate={{ x: enablePromptExpansion ? 22 : 2 }}
                              transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                              className="inline-block h-5 w-5 transform rounded-full shadow-lg"
                              style={{ background: enablePromptExpansion ? '#000' : 'rgba(255,255,255,0.85)' }}
                            />
                          </motion.button>
                        </motion.div>
                      )}

                      {/* Formato Output */}
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                      >
                        <label className="label-sm mb-3 block">Formato Output</label>
                        <div className="flex gap-2">
                          {["jpeg", "png", "webp"].map((format) => (
                            <motion.button
                              key={format}
                              type="button"
                              onClick={() => setValue("format", format)}
                              className={`px-4 py-2 rounded-xl text-xs font-bold transition-all duration-200 ${
                                watch("format") === format ? "pill-active" : "pill-ghost"
                              }`}
                              whileHover={{ scale: 1.04 }}
                              whileTap={{ scale: 0.96 }}
                            >
                              {format.toUpperCase()}
                            </motion.button>
                          ))}
                        </div>
                      </motion.div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>

            {/* Submit button */}
            <motion.button
              type="submit"
              disabled={isGenerating}
              whileHover={!isGenerating ? { y: -2 } : {}}
              whileTap={!isGenerating ? { scale: 0.98 } : {}}
              className={`py-4 px-6 rounded-2xl text-sm ${isGenerating ? 'generate-btn-loading' : 'generate-btn'}`}
            >
              {isGenerating ? (
                <>
                  <div className="w-4 h-4 border-2 rounded-full animate-spin" style={{ borderColor: 'rgba(255,255,255,0.15)', borderTopColor: 'rgba(255,255,255,0.60)' }} />
                  Generazione in corso...
                </>
              ) : (
                <>
                  <Wand2 size={17} />
                  Genera Immagine · {requiredCredits} crediti
                </>
              )}
            </motion.button>
          </form>
        </div>
      </motion.div>
      </div>

      <div className={mobileTab === 'form' ? 'hidden xl:block' : ''}>
      {/* Results Panel */}
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="space-y-6"
      >
        {/* Banner Crediti Insufficienti - Versione Compatta con più Rosso */}
        <AnimatePresence>
          {showCreditsBanner && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ type: "spring", damping: 25, stiffness: 400 }}
              className="relative mb-4"
            >
              <div className="error-banner rounded-2xl p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-xl" style={{ background: 'rgba(255,50,50,0.12)', border: '1px solid rgba(255,80,80,0.20)' }}>
                      <AlertTriangle size={15} style={{ color: 'rgba(255,130,130,1)' }} />
                    </div>
                    <div>
                      <p className="text-white font-medium text-sm">
                        Crediti insufficienti · {credits}/{requiredCredits}
                      </p>
                      <p className="text-xs" style={{ color: 'rgba(255,130,130,0.65)' }}>
                        Ricarica per continuare
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <motion.button
                      onClick={() => navigate('/credits')}
                      className="pill-active px-3 py-1.5 rounded-xl text-xs font-bold flex items-center gap-1.5"
                      whileHover={{ scale: 1.04 }}
                      whileTap={{ scale: 0.96 }}
                    >
                      <CreditCard size={11} />
                      Ricarica
                    </motion.button>

                    <motion.button
                      onClick={() => setShowCreditsBanner(false)}
                      className="p-1.5 rounded-lg transition-colors"
                      style={{ color: 'rgba(255,130,130,0.60)' }}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <X size={13} />
                    </motion.button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Galleria Risultati */}
        <div className="panel rounded-3xl p-7">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="flex items-center justify-between mb-6"
          >
            <div className="flex items-center gap-3">
              <div className="p-3 icon-box rounded-2xl">
                <Sparkles style={{ color: 'rgba(255,255,255,0.80)' }} size={20} />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'var(--font-heading)', letterSpacing: '-0.025em' }}>
                  Risultati
                </h3>
                <p className="text-sm" style={{ color: 'rgba(255,255,255,0.38)' }}>
                  {generatedImages.length} {generatedImages.length === 1 ? 'immagine' : 'immagini'} generate
                </p>
              </div>
            </div>
            {generatedImages.length > 0 && (
              <button
                onClick={() => setGeneratedImages([])}
                className="pill-ghost px-3 py-1.5 rounded-xl text-xs"
              >
                Pulisci
              </button>
            )}
          </motion.div>
          
          <div className="space-y-6">
            {/* ZONA PRINCIPALE - Sempre presente */}
            <div className="relative">
              {isGenerating ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="w-full aspect-square rounded-2xl flex items-center justify-center shimmer-bg"
                  style={{ border: '1px solid rgba(255,255,255,0.08)' }}
                >
                  <div className="flex flex-col items-center">
                    <div className="relative w-24 h-24 mb-6">
                      <motion.div
                        className="absolute inset-0 rounded-full"
                        style={{ border: '2px solid rgba(255,255,255,0.08)' }}
                        animate={{ rotate: 360 }}
                        transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
                      />
                      <motion.div
                        className="absolute inset-3 rounded-full"
                        style={{ border: '2px solid rgba(255,255,255,0.04)', borderTopColor: 'rgba(255,255,255,0.55)' }}
                        animate={{ rotate: -360 }}
                        transition={{ duration: 2.5, repeat: Infinity, ease: "linear" }}
                      />
                      <motion.div
                        className="absolute inset-6 rounded-full"
                        style={{ border: '1.5px solid rgba(255,255,255,0.06)', borderRightColor: 'rgba(255,255,255,0.35)' }}
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1.8, repeat: Infinity, ease: "linear" }}
                      />
                      <motion.div
                        className="absolute inset-0 flex items-center justify-center"
                        animate={{ scale: [1, 1.2, 1], opacity: [0.5, 1, 0.5] }}
                        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                      >
                        <Sparkles size={28} style={{ color: 'rgba(255,255,255,0.65)' }} />
                      </motion.div>
                    </div>
                    <motion.h4
                      className="text-base font-semibold text-white mb-2"
                      style={{ fontFamily: 'var(--font-heading)', letterSpacing: '-0.02em' }}
                      animate={{ opacity: [0.6, 1, 0.6] }}
                      transition={{ duration: 1.8, repeat: Infinity }}
                    >
                      Generazione in corso...
                    </motion.h4>
                    <motion.p
                      className="text-sm text-center"
                      style={{ color: 'rgba(255,255,255,0.35)' }}
                      animate={{ opacity: [0.4, 0.7, 0.4] }}
                      transition={{ duration: 2.2, repeat: Infinity }}
                    >
                      La tua immagine sta prendendo forma
                    </motion.p>
                  </div>
                </motion.div>
              ) : generatedImages.length > 0 ? (
                // Immagine più recente (solo se non stiamo generando)
                <motion.div
                  key={`main-${generatedImages[0]}`}
                  initial={{ opacity: 0, scale: 0.9, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  transition={{ duration: 0.6, ease: "easeOut" }}
                  className="relative group"
                >
                  <div className="relative w-full aspect-square rounded-2xl overflow-hidden bg-gray-800/50 border border-gray-700/50">
                    <img
                      src={generatedImages[0]}
                      alt="Immagine generata più recente"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    
                    {/* Gradient Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="absolute inset-0 flex items-center justify-center gap-3 opacity-0 group-hover:opacity-100 transition-all duration-300">
                    <motion.button 
                      onClick={() => downloadImage(generatedImages[0], 0)}
                      className="p-3 bg-black/50 backdrop-blur-md rounded-2xl hover:bg-black/70 transition-colors text-white shadow-lg"
                      title="Scarica immagine"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <Download size={20} />
                    </motion.button>
                    
                    <motion.button 
                      onClick={() => copyImageUrl(generatedImages[0])}
                      className="p-3 bg-black/50 backdrop-blur-md rounded-2xl hover:bg-black/70 transition-colors text-white shadow-lg"
                      title="Copia URL"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <Copy size={20} />
                    </motion.button>
                    
                    <motion.button 
                      onClick={() => shareImage(generatedImages[0])}
                      className="p-3 bg-black/50 backdrop-blur-md rounded-2xl hover:bg-black/70 transition-colors text-white shadow-lg"
                      title="Condividi"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <Share2 size={20} />
                    </motion.button>
                    
                    <motion.button 
                      onClick={() => setSelectedImageIndex(0)}
                      className="p-3 bg-black/50 backdrop-blur-md rounded-2xl hover:bg-black/70 transition-colors text-white shadow-lg"
                      title="Visualizza a schermo intero"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <Maximize2 size={20} />
                    </motion.button>
                  </div>
                  
                  
                  {/* Image Info */}
                  <div className="absolute bottom-4 left-4 right-4 bg-black/50 backdrop-blur-md rounded-xl p-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="flex items-center justify-between text-white text-sm">
                      <span className="font-medium">Ultima generazione</span>
                      <div className="flex items-center gap-2">
                        <span className="text-xs bg-white/20 px-2 py-1 rounded-lg">
                          {watch("width")}×{watch("height")}
                        </span>
                        <span className="text-xs bg-white/20 px-2 py-1 rounded-lg uppercase">
                          {watch("format")}
                        </span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ) : (
                <div className="w-full aspect-square rounded-2xl flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.02)', border: '1px dashed rgba(255,255,255,0.10)' }}>
                  <div className="text-center">
                    <motion.div
                      animate={{ opacity: [0.3, 0.6, 0.3] }}
                      transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                      className="mb-5"
                    >
                      <ImageIcon size={48} style={{ color: 'rgba(255,255,255,0.22)' }} className="mx-auto" />
                    </motion.div>
                    <p className="font-semibold text-white text-sm mb-1" style={{ fontFamily: 'var(--font-heading)', letterSpacing: '-0.02em' }}>
                      Pronto a generare
                    </p>
                    <p className="text-xs" style={{ color: 'rgba(255,255,255,0.30)' }}>
                      La tua creazione apparirà qui
                    </p>
                  </div>
                </div>
              )}
            </div>
            
            {/* ZONA STORICO - Mostra tutte le immagini quando stiamo generando, o tutte tranne la prima quando non stiamo generando */}
            {((isGenerating && generatedImages.length > 0) || (!isGenerating && generatedImages.length > 1)) && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="space-y-4"
              >
                <div className="flex items-center gap-3" style={{ color: 'rgba(255,255,255,0.40)' }}>
                  <Clock size={14} />
                  <span className="text-xs font-bold uppercase tracking-[0.14em]">Storico</span>
                  <div className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.08)' }} />
                  <span className="pill-ghost text-xs px-3 py-1 rounded-full">
                    {isGenerating ? generatedImages.length : generatedImages.length - 1}
                  </span>
                </div>
                
                {/* Griglia compatta per le immagini precedenti */}
                <div className="grid grid-cols-3 gap-3">
                  {(isGenerating ? generatedImages : generatedImages.slice(1)).map((image, index) => (
                    <motion.div
                      key={`history-${image}-${isGenerating ? index : index + 1}`}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.4, delay: index * 0.1 }}
                      className="relative group cursor-pointer"
                    >
                      <div className="relative aspect-square rounded-xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)' }}>
                        <img
                          src={image}
                          alt={`Immagine generata ${isGenerating ? index + 1 : index + 2}`}
                          className="w-full h-full object-cover transition-all duration-300 group-hover:scale-110"
                        />
                        
                        {/* Overlay scuro con numero */}
                        <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-colors duration-300">
                          <div className="absolute top-2 left-2 bg-black/70 backdrop-blur-sm rounded-lg px-2 py-1">
                            <span className="text-white text-xs font-bold">#{isGenerating ? index + 1 : index + 2}</span>
                          </div>
                          
                          {/* Pulsanti al centro */}
                          <div className="absolute inset-0 flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                            {/* Pulsante Download */}
                            <motion.button
                              onClick={(e) => {
                                e.stopPropagation();
                                downloadImage(image, isGenerating ? index : index + 1);
                              }}
                              className="p-2 bg-black/70 backdrop-blur-md rounded-full hover:bg-black/90 transition-colors text-white shadow-lg"
                              title="Scarica immagine"
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                            >
                              <Download size={14} />
                            </motion.button>
                            
                            {/* Pulsante Ingrandisci */}
                            <motion.button
                              onClick={() => setSelectedImageIndex(isGenerating ? index : index + 1)}
                              className="p-2 bg-black/70 backdrop-blur-md rounded-full hover:bg-black/90 transition-colors text-white shadow-lg"
                              title="Visualizza a schermo intero"
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                            >
                              <Maximize2 size={14} />
                            </motion.button>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </motion.div>
      </div>
      </div>

      {/* InPaint Modal */}
      <InPaint
        isOpen={showInPaint}
        onClose={() => setShowInPaint(false)}
        imageUrl={imagePreview}
        onMaskCreated={handleMaskCreated}
        existingMask={maskUrl}
      />

      {/* Fullscreen Image Modal - Versione Migliorata */}
      <AnimatePresence>
        {selectedImageIndex !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-xl flex items-center justify-center p-2 sm:p-6"
            onClick={() => setSelectedImageIndex(null)}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0, y: 50 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.8, opacity: 0, y: 50 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="relative max-w-5xl w-full max-h-[90vh] group"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Container principale con effetto glass */}
              <div className="relative bg-white/5 backdrop-blur-2xl rounded-3xl border border-white/10 shadow-2xl overflow-hidden">
                {/* Immagine con dimensioni controllate */}
                <div className="relative flex items-center justify-center p-6">
                  <img
                    src={generatedImages[selectedImageIndex]}
                    alt={`Generated image ${selectedImageIndex + 1}`}
                    className="max-w-full max-h-[70vh] object-contain rounded-2xl shadow-xl"
                  />
                </div>
                
                {/* Barra dei controlli con effetto liquid glass */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="absolute bottom-4 left-4 right-4 sm:left-1/2 sm:right-auto sm:bottom-6 sm:-translate-x-1/2 bg-black/20 backdrop-blur-xl rounded-2xl border border-white/10 shadow-2xl"
                >
                  <div className="flex items-center justify-center gap-2 sm:gap-3 px-4 sm:px-6 py-3 sm:py-4">
                    <motion.button
                      onClick={() => downloadImage(generatedImages[selectedImageIndex], selectedImageIndex)}
                      className="p-3 rounded-xl text-white transition-all duration-200"
                      style={{ background: 'rgba(255,255,255,0.10)', border: '1px solid rgba(255,255,255,0.16)' }}
                      title="Scarica immagine"
                      whileHover={{ scale: 1.08, y: -2, background: 'rgba(255,255,255,0.18)' }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Download size={18} />
                    </motion.button>

                    <motion.button
                      onClick={() => copyImageUrl(generatedImages[selectedImageIndex])}
                      className="p-3 rounded-xl text-white transition-all duration-200"
                      style={{ background: 'rgba(255,255,255,0.10)', border: '1px solid rgba(255,255,255,0.16)' }}
                      title="Copia URL"
                      whileHover={{ scale: 1.08, y: -2, background: 'rgba(255,255,255,0.18)' }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Copy size={18} />
                    </motion.button>

                    <motion.button
                      onClick={() => shareImage(generatedImages[selectedImageIndex])}
                      className="p-3 rounded-xl text-white transition-all duration-200"
                      style={{ background: 'rgba(255,255,255,0.10)', border: '1px solid rgba(255,255,255,0.16)' }}
                      title="Condividi"
                      whileHover={{ scale: 1.08, y: -2, background: 'rgba(255,255,255,0.18)' }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Share2 size={18} />
                    </motion.button>

                    {/* Separatore + Info — solo da sm in su */}
                    <div className="hidden sm:block w-px h-8 bg-white/20 mx-2" />
                    <div className="hidden sm:flex items-center gap-3 text-white/80 text-sm">
                      <div className="flex items-center gap-1">
                        <span className="text-white/60">#</span>
                        <span className="font-medium">{selectedImageIndex + 1}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-1 bg-white/10 rounded-lg text-xs font-medium">
                          {watch("width")}×{watch("height")}
                        </span>
                        <span className="px-2 py-1 bg-white/10 rounded-lg text-xs font-medium uppercase">
                          {watch("format")}
                        </span>
                      </div>
                    </div>
                  </div>
                </motion.div>
                
                {/* Pulsante chiudi migliorato */}
                <motion.button
                  onClick={() => setSelectedImageIndex(null)}
                  className="absolute top-4 right-4 p-3 bg-black/30 hover:bg-black/50 backdrop-blur-xl rounded-full text-white border border-white/20 transition-all duration-300 shadow-lg hover:shadow-xl"
                  title="Chiudi"
                  whileHover={{ scale: 1.1, rotate: 90 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <X size={20} />
                </motion.button>
                
                {/* Navigazione tra immagini */}
                {generatedImages.length > 1 && (
                  <>
                    {/* Freccia sinistra */}
                    {selectedImageIndex > 0 && (
                      <motion.button
                        onClick={() => setSelectedImageIndex(selectedImageIndex - 1)}
                        className="absolute left-4 top-1/2 transform -translate-y-1/2 p-3 bg-black/30 hover:bg-black/50 backdrop-blur-xl rounded-full text-white border border-white/20 transition-all duration-300 shadow-lg hover:shadow-xl"
                        title="Immagine precedente"
                        whileHover={{ scale: 1.1, x: -5 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <ChevronLeft size={24} />
                      </motion.button>
                    )}
                    
                    {/* Freccia destra */}
                    {selectedImageIndex < generatedImages.length - 1 && (
                      <motion.button
                        onClick={() => setSelectedImageIndex(selectedImageIndex + 1)}
                        className="absolute right-4 top-1/2 transform -translate-y-1/2 p-3 bg-black/30 hover:bg-black/50 backdrop-blur-xl rounded-full text-white border border-white/20 transition-all duration-300 shadow-lg hover:shadow-xl"
                        title="Immagine successiva"
                        whileHover={{ scale: 1.1, x: 5 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <ChevronRight size={24} />
                      </motion.button>
                    )}
                  </>
                )}
              </div>
              
              {/* Indicatore posizione */}
              {generatedImages.length > 1 && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className="absolute bottom-2 left-1/2 transform -translate-x-1/2 bg-black/40 backdrop-blur-md rounded-full px-3 py-1 text-white/80 text-sm font-medium border border-white/20"
                >
                  {selectedImageIndex + 1} di {generatedImages.length}
                </motion.div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}